import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { useVerifyStripeOAuth } from '../hooks/useStripeConnect';

export const StripeCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mutate: verifyOAuth } = useVerifyStripeOAuth();
  const hasAttempted = useRef(false);

  useEffect(() => {
    // Prevent double-firing in React 18 Strict Mode
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state'); // We pass the nodeId in the state parameter
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle user denial or Stripe errors
    if (error) {
      toast.error(errorDescription || 'Stripe connection was canceled or failed.');
      navigate('/settings', { replace: true });
      return;
    }

    // Validate URL parameters
    if (!code) {
      // Express Connect returns directly without an OAuth code parameter
      toast.success('Stripe account onboarding complete!');
      navigate('/settings/payouts', { replace: true });
      return;
    }

    if (!state) {
      toast.error('Invalid Stripe callback URL. Missing organization identifier.');
      navigate('/settings/payouts', { replace: true });
      return;
    }

    // Verify the code with the backend
    verifyOAuth(
      { nodeId: state, code },
      {
        onSuccess: () => {
          toast.success('Stripe successfully connected!');
          navigate('/settings/payouts', { replace: true });
        },
        onError: () => {
          toast.error('Failed to verify Stripe connection. Please try again.');
          navigate('/settings/payouts', { replace: true });
        },
      }
    );
  }, [searchParams, navigate, verifyOAuth]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 dark:bg-slate-950">
      <Loader2 className="w-12 h-12 text-brand-primary animate-spin mb-4" />
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 text-center">
        Verifying your Stripe account, please wait...
      </h1>
      <p className="text-gray-500 text-center">
        This should only take a moment. You will be redirected automatically.
      </p>
    </div>
  );
};